import {
    subject
} from '@casl/ability';
import {
    Box,
    Divider,
    Link as MUILink,
    useMediaQuery,
    useTheme
} from '@mui/material';
import axios from 'axios';
import {
    addDays,
    format
} from 'date-fns';
import {
    type FC,
    memo,
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    AdSlot,
    DFPSlotsProvider
} from 'react-dfp';
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams
} from 'react-router-dom';
import Slider, {
    type Settings
} from 'react-slick';
import {
    toast
} from 'react-toastify';

import Claim from '../components/Claim';
import Section from '../components/Section';
import SubscribeToast from '../components/SubscribeToast';
import Supporters from '../components/Supporters';
import type {
    Bulletin,
    Contract
} from '../types';

import Button from '@components/typed/Button';
import Typography from '@components/typed/Typography';

import Loader from '@components/shared/Loader';

import Flipbook from '@components/flipbook/View/Flipbook';

import {
    AppContext
} from '@contexts/AppContext';

import customerDataRequest from '@services/api/customer-data';

import {
    getAuthToken,
    loadListingAbilitiesForUser
} from '@auth/authHelpers';
import {
    AbilityContext
} from '@auth/Can';
import {
    useAppAuth
} from '@auth/useAppAuth';

import {
    getPdfUrlByPidCallback
} from '@utils/organization';
import {
    isThemeOrganization
} from '@utils/theme';
import useSlickAriaHiddenFix from '@utils/useSlickAriaHiddenFix';

import {
    Descriptions
} from '@constants/Descriptions';
import {
    FULL_PUBLICATION_FORMAT
} from '@constants/publications';
import {
    ERROR_404,
    ERROR_500
} from '@constants/routes';
import {
    COLORS,
    POL_THEME,
    ThemeContext
} from '@constants/theme';
import {
    Titles
} from '@constants/titles';

import type {
    Organization
} from '@types';

import type {
    PathParams,
    ViewBulletinProps
} from './types';

import styles from './styles';

import THEMED_ADVERTISEMENT_SLOTS from './advertisements';
import usePublicationAdvertisements from './usePublicationAdvertisements';

const BULLETIN_LIMIT = 12;

// We build a publication URL based on theme and path-friendly slug
const buildUrlPublication = (publication: string | null, isPol: boolean) => {
    if (!publication) {
        return null;
    }

    const root = isPol ? process.env.POL_BULLETINS_ROOT_URL : process.env.MCO_BULLETINS_ROOT_URL;

    return `${root}/${publication.replace(/-/g, '/')}`;
};

// We format the publish date adding one day for display rules
const getDisplayDate = (isoDate: string) => format(addDays(new Date(isoDate), 1), FULL_PUBLICATION_FORMAT);

// We normalize cross-domain file URLs when not on POL
const toMcoIfNeeded = (url: string | null, isPol: boolean) => {
    if (!url || isPol) {
        return url;
    }

    try {
        const u = new URL(url);

        if (u.hostname.includes('parishesonline')) {
            u.hostname = u.hostname.replace('parishesonline', 'mycommunityonline');
        }

        return u.toString();
    } catch {
        return url; // leave as-is on malformed URL
    }
};

const mobile: Settings = {
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: 2,
    slidesToShow: 2.5,
    speed: 500,
    vertical: false
};

const desktop: Settings = {
    arrows: true,
    dots: false,
    infinite: false,
    slidesToScroll: 5,
    slidesToShow: 5,
    speed: 500,
    vertical: false
};

const ViewBulletin: FC<ViewBulletinProps> = () => {
    const navigate = useNavigate();

    const {
        filename,
        slug
    } = useParams<PathParams>();

    const [
        searchParams
    ] = useSearchParams();

    // We access theme and responsive utilities
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // We tap app contexts
    const themeContext = useContext(ThemeContext);
    const {
        setPageDescription,
        setPageTitle
    } = useContext(AppContext);
    const contextAbility = useContext(AbilityContext);

    // We tap authentication
    const {
        getAccessToken,
        isAuthenticated,
        isLoading: isAuthenticationLoading,
        user
    } = useAppAuth();

    // We compute theme guards
    const isPOL = themeContext === POL_THEME;

    // We read URL query parameters
    const pid = searchParams.get('pid');
    const pub = searchParams.get('publication');
    const selectedPublicationUrlParam = searchParams.get('selectedPublication');

    // We derive publication path from params
    const publicationParam = filename ?? pub;

    // We derive canonical publication URL for current theme
    const urlPublication = useMemo(() => buildUrlPublication(publicationParam, isPOL), [
        publicationParam,
        isPOL
    ]);

    const advertisementSlot = THEMED_ADVERTISEMENT_SLOTS[themeContext || 'POL'];

    // We prefer explicit selected URL, falling back to derived one
    const urlSelectedPublication = useMemo(() => {
        const fromParam = toMcoIfNeeded(selectedPublicationUrlParam, isPOL);

        if (fromParam) {
            return fromParam;
        }

        if (urlPublication) {
            return urlPublication;
        }

        return null;
    }, [
        selectedPublicationUrlParam,
        urlPublication,
        isPOL
    ]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        organizationDetails,
        setOrganizationDetails
    ] = useState<Organization | undefined>(undefined);

    const [
        contractsList,
        setContractsList
    ] = useState<Contract[] | undefined>(undefined);

    const [
        bulletinsList,
        setBulletinsList
    ] = useState<Bulletin[]>([]);

    const [
        viewerPublication,
        setViewerPublication
    ] = useState<Bulletin | null>(null);

    const [
        publicationURL,
        setPublicationURL
    ] = useState<string | null>(null);

    const [
        prettyPublishDate,
        setPrettyPublishDate
    ] = useState<string | null>(null);

    const [
        canAdminPage,
        setCanAdminPage
    ] = useState<boolean>(false);

    const [
        canAdminAll,
        setCanAdminAll
    ] = useState<boolean>(false);

    const featuredSlot = isMobile ? advertisementSlot.Publication_300x100_Mobile : advertisementSlot.Publication_XL_300x600;
    const wcSponsorSlot = isMobile ? advertisementSlot.Publication_WCSponsor_300x100_Mobile : advertisementSlot.Publication_WCSponsor_728x90;
    const adSessionKey = `${themeContext}-${slug}-${organizationDetails?.salesforce_id}-${isMobile ? 'm' : 'd'}`;
    const {
        isFeaturedAdEmpty,
        isWCSponsorAdEmpty,
        onSlotRegister,
        onSlotRender,
        targetingArguments
    } = usePublicationAdvertisements({
        contractsList,
        featuredSlot,
        isReady: !isLoading && !isAuthenticationLoading && organizationDetails?.slug === slug,
        organizationDetails,
        sessionKey: adSessionKey,
        wcSponsorSlot
    });

    const isPageHydrated = useRef<boolean>(false);

    const organizationDetailsRequest = useRef<Promise<void> | null>(null);
    const contractsRequest = useRef<Promise<void> | null>(null);
    const adminRightsRequest = useRef<Promise<void> | null>(null);
    const bulletinsRequest = useRef<Promise<void> | null>(null);
    const carouselRef = useRef<HTMLDivElement | null>(null);
    const initialLoadRef = useRef<boolean>(true);
    const topRef = useRef<HTMLDivElement | null>(null);

    const sliderSettings = useSlickAriaHiddenFix(
        carouselRef,
        isMobile ? mobile : desktop,
        {
            focusableSelector: 'a'
        }
    );

    // Early redirect if nothing requested
    useEffect(() => {
        if (isAuthenticationLoading) {
            return;
        }

        if (!pid && !pub && !filename && !selectedPublicationUrlParam) {
            toast.error('No publication was requested.');

            startTransition(() => navigate(`/organization/${slug}`));
        }
    }, [
        filename,
        isAuthenticationLoading,
        navigate,
        pid,
        pub,
        selectedPublicationUrlParam,
        slug
    ]);

    // Scroll to top when URL query params change, reset focus for screen readers
    useEffect(() => {
        window.scrollTo(0, 0);

        // If this is the first render, skip focus reset
        if (initialLoadRef.current) {
            initialLoadRef.current = false;

            return;
        }

        topRef.current?.focus();
    }, [
        pid,
        pub,
        selectedPublicationUrlParam
    ]);

    // Force a fresh hydration for the new organization
    useEffect(() => {
        isPageHydrated.current = false;
        setOrganizationDetails(undefined);
        setContractsList(undefined);
        setBulletinsList([]);
        setViewerPublication(null);
        setPublicationURL(null);
        setCanAdminAll(false);
        setCanAdminPage(false);
    }, [
        slug
    ]);

    // Resolve PDF by PID and redirect
    const loadPdfByPid = useCallback(async () => {
        if (!organizationDetails?.salesforce_id || !pid) {
            return;
        }

        const data = await getPdfUrlByPidCallback(
            organizationDetails.salesforce_id,
            pid,
            getAccessToken
        );

        if (data?.pdfUrl) {
            startTransition(() => navigate(data.pdfUrl));
        } else {
            startTransition(() => navigate(`/organization/${slug}`));
        }
    }, [
        organizationDetails,
        pid,
        slug,
        navigate,
        getAccessToken
    ]);

    useEffect(() => {
        loadPdfByPid();
    }, [
        loadPdfByPid
    ]);

    // Maintain pretty publish date for viewer
    useEffect(() => {
        if (!viewerPublication) {
            return;
        }

        if (viewerPublication.publishDate) {
            setPrettyPublishDate(getDisplayDate(viewerPublication.publishDate));
        } else {
            setPrettyPublishDate('Archive');
        }
    }, [
        viewerPublication
    ]);

    //  Update meta tags defensively
    useEffect(() => {
        if (!organizationDetails?.physical_title || !prettyPublishDate) {
            return;
        }

        const parts = prettyPublishDate.split(', ');

        const date = (prettyPublishDate !== 'Archive' && parts.length >= 3) ? `${parts[1]}, ${parts[2]}` : prettyPublishDate;

        if (isPOL) {
            setPageTitle(
                Titles.POL_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );

            setPageDescription(
                Descriptions.POL_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );
        } else {
            setPageDescription(
                Descriptions.MCO_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );

            setPageTitle(
                Titles.MCO_PUBLICATION(
                    organizationDetails.physical_title,
                    date
                )
            );
        }
    }, [
        organizationDetails,
        prettyPublishDate,
        isPOL,
        setPageTitle,
        setPageDescription
    ]);

    const sortedBulletins = useMemo(() => [
        ...bulletinsList
    ].sort((a, b) => new Date(b.publishDate ?? 0).getTime() - new Date(a.publishDate ?? 0).getTime()), [
        bulletinsList
    ]);

    //  Select or validate the requested publication
    useEffect(() => {
        if (!urlSelectedPublication || !sortedBulletins.length) {
            if (!urlSelectedPublication) {
                setViewerPublication(null);
                setPublicationURL(null);
            }

            return undefined;
        }

        const canonicalize = (url: string | null) => toMcoIfNeeded(url, isPOL);
        const normalizedTarget = encodeURI(canonicalize(urlSelectedPublication)!);

        const match = sortedBulletins.find((p) => encodeURI(canonicalize(p.fileUrl)!) === normalizedTarget);

        if (match) {
            setViewerPublication(match);
            setPublicationURL(null);

            return undefined;
        }

        const ctrl = new AbortController();

        (async () => {
            try {
                const response = await fetch(
                    urlSelectedPublication,
                    {
                        method: 'HEAD',
                        signal: ctrl.signal
                    }
                );

                if (response.ok) {
                    setPublicationURL(urlSelectedPublication);
                } else {
                    toast.error('The requested publication is not available.');

                    startTransition(() => navigate(`/organization/${slug}`));
                }
            } catch (error) {
                if (!ctrl.signal.aborted) {
                    toast.error('Encountered an unknown error.');
                }
            }
        })();

        return () => {
            ctrl.abort();
        };
    }, [
        urlSelectedPublication,
        sortedBulletins,
        navigate,
        slug,
        isPOL
    ]);

    //  Data fetchers (deduplicated with refs)
    const fetchOrganizationDetails = useCallback(async () => {
        if (!slug) {
            return undefined;
        }

        if (organizationDetails) {
            return undefined;
        }

        if (organizationDetailsRequest.current) {
            return organizationDetailsRequest.current;
        }

        organizationDetailsRequest.current = (async () => {
            try {
                const {
                    data: {
                        data
                    }
                } = await customerDataRequest<Organization>(
                    'get',
                    `/organizations/slug/${slug}`
                );

                const ok = isThemeOrganization(
                    data,
                    themeContext
                );

                if (!ok) {
                    navigate(ERROR_404);

                    return;
                }

                setOrganizationDetails(data);
            } catch (error) {
                if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
                    navigate(ERROR_404);
                } else {
                    navigate(ERROR_500);
                }
            } finally {
                organizationDetailsRequest.current = null;
            }
        })();

        return organizationDetailsRequest.current;
    }, [
        navigate,
        slug,
        themeContext,
        organizationDetails
    ]);

    const fetchContracts = useCallback(async () => {
        if (!organizationDetails) {
            return undefined;
        }

        if (contractsList) {
            return undefined;
        }

        if (contractsRequest.current) {
            return contractsRequest.current;
        }

        const salesforceID = organizationDetails.salesforce_id;

        contractsRequest.current = (async () => {
            try {
                const {
                    data: {
                        data
                    }
                } = await customerDataRequest<Contract[]>(
                    'get',
                    `organizations/${salesforceID}/contracts`
                );

                setContractsList(data);
            } catch {
                setContractsList([]);
            } finally {
                contractsRequest.current = null;
            }
        })();

        return contractsRequest.current;
    }, [
        organizationDetails,
        contractsList
    ]);

    const fetchAdminRights = useCallback(async () => {
        if (!organizationDetails || !isAuthenticated) {
            return undefined;
        }

        if (adminRightsRequest.current) {
            return adminRightsRequest.current;
        }

        const salesforceID = organizationDetails.salesforce_id;

        adminRightsRequest.current = (async () => {
            try {
                const token = await getAuthToken(getAccessToken);

                const userAbility = await loadListingAbilitiesForUser(
                    user,
                    token,
                    contextAbility
                );

                const fetchedCanAdminAll = userAbility.can(
                    'admin',
                    'all'
                );

                const fetchedCanAdminClaimedPage = userAbility.can(
                    'admin',
                    subject(
                        'claimed',
                        {
                            id: salesforceID
                        }
                    )
                );

                setCanAdminPage(fetchedCanAdminClaimedPage);
                setCanAdminAll(fetchedCanAdminAll);
            } catch {
                setCanAdminPage(false);
                setCanAdminAll(false);
            } finally {
                adminRightsRequest.current = null;
            }
        })();

        return adminRightsRequest.current;
    }, [
        contextAbility,
        getAccessToken,
        isAuthenticated,
        organizationDetails,
        user
    ]);

    const fetchBulletinsList = useCallback(async () => {
        if (!organizationDetails) {
            return undefined;
        }

        if (bulletinsRequest.current) {
            return bulletinsRequest.current;
        }

        bulletinsRequest.current = (async () => {
            try {
                const {
                    data: {
                        data
                    }
                } = await customerDataRequest<Bulletin[]>(
                    'get',
                    `/organizations/${organizationDetails.salesforce_id}/publications?limit=${BULLETIN_LIMIT}&type=${isPOL ? 'Church' : 'Other'}`
                );

                setBulletinsList(data);
            } catch (error) {
                setBulletinsList([]);
            } finally {
                bulletinsRequest.current = null;
            }
        })();

        return bulletinsRequest.current;
    }, [
        isPOL,
        organizationDetails
    ]);

    //  Hydration
    useEffect(() => {
        if (isPageHydrated.current) {
            return undefined;
        }

        let cancelled = false;

        const hydrate = async () => {
            setIsLoading(true);

            await Promise.all([
                fetchContracts(),
                fetchAdminRights(),
                fetchBulletinsList()
            ]);

            isPageHydrated.current = true;

            if (!cancelled) {
                setIsLoading(false);
            }
        };

        if (organizationDetails) {
            hydrate();
        }

        return () => {
            cancelled = true;
        };
    }, [
        organizationDetails,
        fetchBulletinsList,
        fetchContracts,
        fetchAdminRights
    ]);

    //  Fetch organization when slug or theme changes
    useEffect(() => {
        fetchOrganizationDetails();
    }, [
        fetchOrganizationDetails
    ]);

    const previousBulletins = useMemo(() => {
        if (!organizationDetails) {
            return null;
        }

        return sortedBulletins.map(({
            fileUrl,
            publishDate,
            smallThumbnailUrl
        }) => (
            <Box key={fileUrl}>
                <Typography variant={'body2'}>
                    {format(addDays(new Date(publishDate), 1), 'EEE, MMM d')}
                </Typography>
                <MUILink
                    component={Link}
                    sx={styles.thumbnailSmall}
                    to={`/publication-page/${organizationDetails.slug}?selectedPublication=${encodeURIComponent(fileUrl)}`}
                >
                    <Box
                        alt={`Bulletin thumbnail for ${format(addDays(new Date(publishDate), 1), 'MMM d, yyyy')}`}
                        component={'img'}
                        src={smallThumbnailUrl}
                    />
                </MUILink>
            </Box>
        ));
    }, [
        sortedBulletins,
        organizationDetails
    ]);

    const divider = useMemo(() => (
        <Divider
            sx={styles.divider}
        />
    ), []);

    const mobileHeader = !!organizationDetails && (
        <Box
            display={'flex'}
            flexDirection={'column'}
        >
            <Link to={`/organization/${organizationDetails.slug}`}>
                <Typography
                    aria-label={`Back to ${organizationDetails.physical_title}`}
                    variant={'h3'}
                >
                    {`< Back to ${organizationDetails.physical_title}`}
                </Typography>
            </Link>
            <Box
                display={isFeaturedAdEmpty ? 'none' : 'flex'}
                flexGrow={1}
                justifyContent={'center'}
                my={3}
            >
                <AdSlot
                    {...featuredSlot}
                    onSlotRegister={onSlotRegister}
                    onSlotRender={onSlotRender}
                    targetingArguments={targetingArguments}
                />
            </Box>
            <Typography
                sx={{
                    pl: 2
                }}
                variant={'h6'}
            >
                {prettyPublishDate}
            </Typography>
            {
                !!(viewerPublication?.fileUrl || publicationURL) && (
                    <Button
                        component={MUILink}
                        href={viewerPublication?.fileUrl || publicationURL || undefined}
                        rel={'noopener'}
                        sx={{
                            backgroundColor: theme.colors.plantation,
                            color: theme.colors.white,
                            mt: 3
                        }}
                        target={'_blank'}
                        variant={'contained'}
                    >
                        {'View as PDF'}
                    </Button>
                )
            }
        </Box>
    );

    const desktopHeader = !!organizationDetails && (
        <Box
            alignItems={'flex-start'}
            display={'flex'}
            flexDirection={'row'}
            gap={4}
        >
            <Box
                aria-label={`${organizationDetails.physical_title} Publication - ${prettyPublishDate}`}
                component={'h1'}
                sx={styles.header}
            >
                <Link to={`/organization/${organizationDetails.slug}`}>
                    <Typography
                        aria-label={`Back to ${organizationDetails.physical_title}`}
                        component={'span'}
                        variant={'h4'}
                    >
                        {`< Back to ${organizationDetails.physical_title}`}
                    </Typography>
                </Link>
                <Typography
                    component={'div'}
                    sx={{
                        pl: 2.5
                    }}
                    variant={'h5'}
                >
                    {prettyPublishDate}
                </Typography>
            </Box>
            {
                !!(viewerPublication?.fileUrl || publicationURL) && (
                    <Button
                        component={MUILink}
                        href={viewerPublication?.fileUrl || publicationURL || undefined}
                        rel={'noopener'}
                        sx={{
                            backgroundColor: theme.colors.plantation,
                            color: theme.colors.white
                        }}
                        target={'_blank'}
                        variant={'contained'}
                    >
                        {'View as PDF'}
                    </Button>
                )
            }
        </Box>
    );

    return (
        (isLoading || !organizationDetails || organizationDetails.slug !== slug || isAuthenticationLoading) ? (
            <Loader
                color={COLORS.secondary}
            />
        ) : (
            <DFPSlotsProvider
                dfpNetworkId={process.env.DFP_NETWORK_ID!}
                disableInitialLoad
                key={adSessionKey}
                singleRequest
            >
                <Box
                    component={'main'}
                    ref={topRef}
                    sx={styles.wrapper}
                    tabIndex={-1}
                >
                    <SubscribeToast
                        organizationDetails={organizationDetails}
                    />
                    <Box
                        sx={styles.container}
                    >
                        <Section>
                            { isMobile ? mobileHeader : desktopHeader }
                        </Section>
                        <Box
                            display={'flex'}
                            gap={{
                                lg: 8,
                                xl: 28
                            }}
                            justifyContent={'space-between'}
                        >
                            <Box
                                flex={1}
                                mt={{
                                    md: 8,
                                    xs: 3
                                }}
                            >
                                <Flipbook pdfUrl={viewerPublication?.fileUrl || publicationURL} />
                            </Box>
                            {
                                !isMobile && (
                                    <Box
                                        alignItems={'center'}
                                        display={'flex'}
                                        flexDirection={'column'}
                                        gap={1}
                                        width={300}
                                    >
                                        {/* This will be reinstated shortly */}
                                        {/* <Typography
                                            textAlign={'center'}
                                            variant={'h6'}
                                        >
                                            <b>{'Featured Supporter'}</b>
                                            {` of ${organizationDetails.physical_title}`}
                                        </Typography>
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            size={'2x'}
                                        /> */}
                                        <AdSlot
                                            {...featuredSlot}
                                            onSlotRegister={onSlotRegister}
                                            onSlotRender={onSlotRender}
                                            targetingArguments={targetingArguments}
                                        />
                                    </Box>
                                )
                            }
                        </Box>
                        {
                            !!sortedBulletins.length && (
                            <>
                                {divider}
                                <Section horizontalPadding={'deep'}>
                                    <Typography
                                        ml={{
                                            md: '-18px !important'
                                        }}
                                        variant={'subtitle1'}
                                    >
                                        {'Recent Publications'}
                                    </Typography>
                                    <Box
                                        aria-label={'Recent publications'}
                                        aria-roledescription={'carousel'}
                                        ref={carouselRef}
                                        role={'group'}
                                        sx={styles.carousel}
                                    >
                                        <Slider {...sliderSettings}>
                                            {previousBulletins}
                                        </Slider>
                                    </Box>
                                </Section>
                            </>
                            )
                        }
                        {!isWCSponsorAdEmpty && (
                            <Divider
                                sx={(muiTheme) => ({
                                    ...styles.divider(muiTheme),
                                    mb: '28px'
                                })}
                            />
                        )}
                        <Box
                            aria-label={'Advertisement'}
                            component={'aside'}
                            sx={[
                                styles.wcSponsorSection,
                                isWCSponsorAdEmpty ? {
                                    display: 'none'
                                } : {}
                            ]}
                        >
                            <AdSlot
                                {...wcSponsorSlot}
                                onSlotRegister={onSlotRegister}
                                onSlotRender={onSlotRender}
                                targetingArguments={targetingArguments}
                            />
                        </Box>
                        {!isWCSponsorAdEmpty && (
                            <Divider
                                sx={(muiTheme) => ({
                                    ...styles.divider(muiTheme),
                                    mt: '28px'
                                })}
                            />
                        )}
                        <Supporters
                            divider={divider}
                            organizationDetails={organizationDetails}
                        />
                        {
                            (!canAdminPage && !canAdminAll) && (
                                <>
                                    {divider}
                                    <Claim
                                        organizationDetails={organizationDetails}
                                    />
                                </>
                            )
                        }
                    </Box>
                </Box>
            </DFPSlotsProvider>
        )
    );
};

export default memo(ViewBulletin);
